// Client-side domain name validation. Returns a normalised ASCII (punycode)
// name or an error code; domainErrorText() maps codes to translated text.
import { t } from './i18n.jsx';

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const TLD = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

/** @returns {{ ok: true, domain: string } | { ok: false, code: string }} */
export function validateDomain(input) {
  const raw = String(input ?? '').trim().toLowerCase().replace(/\.$/, '');
  if (!raw) return { ok: false, code: 'empty' };
  if (/^[a-z][a-z0-9+.-]*:\/\//.test(raw) || raw.includes('/') || raw.includes('?') || raw.includes('#')) {
    return { ok: false, code: 'url' };
  }
  if (raw.includes('@')) return { ok: false, code: 'email' };
  if (/\s/.test(raw)) return { ok: false, code: 'space' };
  if (raw.startsWith('www.')) return { ok: false, code: 'www' };
  if (!raw.includes('.')) return { ok: false, code: 'no_tld' };

  // Convert internationalised names to punycode the way browsers do.
  let ascii = raw;
  if (/[^\x00-\x7f]/.test(raw)) {
    try {
      ascii = new URL(`http://${raw}`).hostname;
    } catch {
      return { ok: false, code: 'chars' };
    }
  }
  if (ascii.length > 253) return { ok: false, code: 'too_long' };

  const labels = ascii.split('.');
  if (labels.some((l) => l.length === 0)) return { ok: false, code: 'empty_label' };
  if (labels.some((l) => l.length > 63)) return { ok: false, code: 'label_too_long' };
  const tld = labels[labels.length - 1];
  if (!TLD.test(tld)) return { ok: false, code: 'tld' };
  for (const l of labels.slice(0, -1)) {
    if (!/^[a-z0-9-]+$/.test(l)) return { ok: false, code: 'chars' };
    if (!LABEL.test(l)) return { ok: false, code: 'hyphen' };
  }
  return { ok: true, domain: ascii };
}

const DOMAIN_ERROR_CODES = ['empty', 'url', 'email', 'space', 'www', 'no_tld', 'too_long', 'empty_label', 'label_too_long', 'tld', 'chars', 'hyphen'];

// Translated message for a validateDomain() error code.
export function domainErrorText(code, lang) {
  return t(lang, `domainError.${DOMAIN_ERROR_CODES.includes(code) ? code : 'chars'}`);
}
